import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button
} from '@mui/material';
import HomeLayout from '../components/layout/HomeLayout';
import { useAuth } from '../features/auth';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BuildIcon from '@mui/icons-material/Build';
import StorageIcon from '@mui/icons-material/Storage';
import GroupIcon from '@mui/icons-material/Group';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { getAvatarUrl } from '../utils/helpers';

const HomePage = () => {
  const { user } = useAuth();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Chào buổi sáng';
    if (hour < 18) return 'Chào buổi chiều';
    return 'Chào buổi tối';
  };

  return (
    <HomeLayout>
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 4 } }}>
        <Paper
          elevation={0}
          sx={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: 4,
            p: { xs: 2.5, sm: 4, md: 6 },
            color: 'white',
            mb: 4,
            border: '1px solid rgba(255,255,255,0.1)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Decorative circle */}
          <Box
            sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              borderRadius: '50%',
              bgcolor: 'rgba(59, 130, 246, 0.1)',
              zIndex: 0
            }}
          />

          <Box sx={{ 
            position: 'relative', 
            zIndex: 1, 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center', 
            textAlign: { xs: 'center', md: 'left' },
            gap: { xs: 2, md: 4 },
            p: 0
          }}>
            <Avatar
              sx={{
                width: { xs: 80, sm: 100, md: 120 },
                height: { xs: 80, sm: 100, md: 120 },
                bgcolor: 'primary.main',
                fontSize: { xs: '1.5rem', md: '2.5rem' },
                fontWeight: 'bold',
                boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                border: '4px solid rgba(255,255,255,0.1)'
              }}
              src={getAvatarUrl(user?.avatar)}
            >
              {user?.username?.charAt(0)?.toUpperCase() || '?'}
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight={800} gutterBottom sx={{ 
                fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.5rem' },
                background: 'linear-gradient(45deg, #60A5FA, #A78BFA)', 
                backgroundClip: 'text', 
                textFillColor: 'transparent', 
                WebkitBackgroundClip: 'text', 
                WebkitTextFillColor: 'transparent' 
              }}>
                {user ? `${getGreeting()}, ${user.fullName || user.username}!` : 'Chào mừng bạn đến với GPT Team!'}
              </Typography>
              <Typography variant="h6" sx={{ 
                fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' },
                color: '#94A3B8', 
                fontWeight: 400 
              }}>
                {user ? 'Chào mừng bạn quay trở lại hệ thống quản lý GPT Team.' : 'Vui lòng đăng nhập để quản lý và sử dụng các dịch vụ của chúng tôi.'}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: '#F1F5F9' }}>
          Tính Năng Nổi Bật
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3, mb: 6 }}>
          <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#1E293B', border: '1px solid #334155', display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(59, 130, 246, 0.1)', color: '#60A5FA' }}>
              <StorageIcon />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} color="#F8FAFC" gutterBottom>Lưu trữ Host Không Giới Hạn</Typography>
              <Typography color="#94A3B8" variant="body2">Dễ dàng thêm và quản lý hàng loạt tài khoản GPT mà không lo giới hạn lưu trữ phụ thuộc vào gói cước đăng ký.</Typography>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#1E293B', border: '1px solid #334155', display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(167, 139, 250, 0.1)', color: '#A78BFA' }}>
              <GroupIcon />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} color="#F8FAFC" gutterBottom>Chia sẻ linh hoạt</Typography>
              <Typography color="#94A3B8" variant="body2">Tạo mã mời và cấp phát quyền sử dụng Host cho thành viên khác nhanh chóng, dễ dàng theo dõi lượng truy cập.</Typography>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#1E293B', border: '1px solid #334155', display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(74, 222, 128, 0.1)', color: '#4ADE80' }}>
              <SpeedIcon />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} color="#F8FAFC" gutterBottom>Đồng Bộ Realtime</Typography>
              <Typography color="#94A3B8" variant="body2">Tự động làm mới Token ChatGPT và đồng bộ danh sách thành viên thực tế một cách chuẩn xác, liên tục.</Typography>
            </Box>
          </Paper>
        </Box>

        {/* Merged Guide Content */}
        <Box mb={4}>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#fff' }} gutterBottom>
            Hướng Dẫn Bắt Đầu Nhanh
          </Typography>
          <Typography sx={{ color: '#94a3b8' }}>
            4 bước cơ bản để thiết lập và đưa hệ thống vào hoạt động trơn tru.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, bgcolor: '#1e293b', color: 'white', mb: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="flex-start" gap={2}>
                    <Avatar sx={{ bgcolor: 'primary.main', fontWeight: 'bold' }}>1</Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>Nạp quỹ vào Ví</Typography>
                      <Typography variant="body2" color="#94a3b8">
                        Vào mục <b>Lịch sử giao dịch</b> đề nạp tiền tự động qua QR. Tiền sẽ dùng để mua Gói dịch vụ.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="flex-start" gap={2}>
                    <Avatar sx={{ bgcolor: 'secondary.main', fontWeight: 'bold' }}>2</Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>Đăng ký Gói Dịch Vụ</Typography>
                      <Typography variant="body2" color="#94a3b8">
                        Vào mục <b>Gói Dịch Vụ</b> để chọn một Plan phù hợp với nhu cầu. Việc này mở khóa toàn bộ tính năng.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="flex-start" gap={2}>
                    <Avatar sx={{ bgcolor: 'success.main', fontWeight: 'bold' }}>3</Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>Thêm Tài khoản Host</Typography>
                      <Typography variant="body2" color="#94a3b8">
                        Tại <b>Quản lý Host</b>, thêm danh sách GPT accounts của bạn bằng định dạng email|password|2fa_secret.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="flex-start" gap={2}>
                    <Avatar sx={{ bgcolor: 'warning.main', fontWeight: 'bold' }}>4</Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>Chia sẻ & Bắt đầu</Typography>
                      <Typography variant="body2" color="#94a3b8">
                        Tạo mã Mời Thành Viên tại trang Xem Chi Tiết của từng Host và gửi cho user/khách hàng.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: '#1e293b', color: 'white' }}>
              <Box display="flex" alignItems="center" mb={2}>
                <HelpOutlineIcon sx={{ mr: 1, color: '#38bdf8' }} />
                <Typography variant="h6" fontWeight="bold">Câu Hỏi Thường Gặp</Typography>
              </Box>

              <Box sx={{ '& .MuiAccordion-root': { bgcolor: 'transparent', color: 'white', boxShadow: 'none', borderBottom: '1px solid rgba(255,255,255,0.1)' } }}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                    <Typography fontWeight="bold">Làm sao để nạp tiền vào ví?</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography color="#94a3b8">
                      Bạn vui lòng truy cập vào mục <b>Lịch sử giao dịch</b> hoặc nhấn vào số dư trên thanh menu, sau đó chọn <b>Nạp tiền</b>. Hệ thống hỗ trợ nạp tự động qua QR Code.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                    <Typography fontWeight="bold">Tool check host hoạt động như thế nào?</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography color="#94a3b8">
                      Tool sẽ tự động kiểm tra trạng thái live/die của các tài khoản GPT trong danh sách của bạn bằng cách gửi request thực tế. Kết quả sẽ được cập nhật real-time.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                    <Typography fontWeight="bold">Gói cước có tự động gia hạn không?</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography color="#94a3b8">
                      Hiện tại hệ thống không tự động trừ tiền gia hạn. Bạn sẽ nhận được thông báo khi gói cước sắp hết hạn để chủ động gia hạn.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Paper>
          </Grid>

          {/* Right Side: Quick Tools / Support */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, bgcolor: '#1e293b', color: 'white', mb: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <BuildIcon sx={{ mr: 1, color: '#38bdf8' }} />
                <Typography variant="h6" fontWeight="bold">Hỗ Trợ Kỹ Thuật</Typography>
              </Box>
              <Typography variant="body2" color="#94a3b8" paragraph>
                Nếu bạn gặp bất kỳ vấn đề nào trong quá trình sử dụng hệ thống Quản lý GPT, vui lòng nhắn tin trực tiếp tới số Zalo của đội ngũ hỗ trợ để được giải đáp nhanh nhất.
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                sx={{ mb: 2, color: '#0068FF', borderColor: '#0068FF', '&:hover': { borderColor: '#0054cc', bgcolor: 'rgba(0,104,255,0.1)' } }}
                onClick={() => window.open('https://zalo.me/0869680786', '_blank')}
              >
                Chat Zalo: 0869680786
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </HomeLayout>
  );
};

export default HomePage;
